/**
 * MORPHISM SCHEMA LAYER
 * ----------------------------------------------------------------------------
 * Morphism (`morphism`) is a zero-dependency, schema-driven object
 * transformation library: a **schema** declares the shape of your *target*
 * view-model, and each value describes *how* to read it from the *source*.
 * Crucially, Morphism preserves dimension — if you pass an array, it returns
 * an array of transformed items. That means one schema describes a single
 * item and is reused for whole collections with zero repetition.
 *
 * Raw data lives in /public/data/*.json (the editable "stored data").
 * These schemas turn that raw data into exactly the shapes the renderer needs.
 *
 * Value kinds used below:
 *   - `'sourceProp'`           → copy from a (dot-notation) path
 *   - `({ a, b }) => ...`      → derive from the whole source object
 *                                 (e.g. format dates with dayjs, aggregate links)
 */
import Morphism from 'morphism';
import dayjs from 'dayjs';

// morphism ships as a CommonJS bundle; the function lives on the default export.
const { morphism } = Morphism;

// ---------------------------------------------------------------------------
// Date helpers (used inside Morphism schema functions)
// ---------------------------------------------------------------------------
function formatRange(start, end, current) {
  const from = dayjs(start).format('MMM YYYY');
  const to = current ? 'Present' : end ? dayjs(end).format('MMM YYYY') : 'Present';
  return `${from} — ${to}`;
}

// Turns { demo, github } into a uniform, render-ready link list.
const asLinkList = ({ links }) =>
  Object.entries(links || {}).map(([type, href]) => ({
    label: type === 'live' ? 'Live demo' : 'Source code',
    href,
    icon: type === 'live' ? 'live' : 'github',
  }));

// ---------------------------------------------------------------------------
// profile.json  →  profile view-model
// ---------------------------------------------------------------------------
export const profileSchema = {
  name: 'name',
  title: 'title',
  location: 'location',
  email: 'email',
  phone: 'phone',
  photo: 'photo',
  github: 'github',
  linkedin: 'linkedin',
  bio: 'bio',
  // Aggregate the four required contact channels (GitHub, Mail, Phone, LinkedIn)
  // into one uniform array so the footer / hero / contact section share it.
  contactLinks: ({ github, linkedin, email, phone }) => [
    { label: 'GitHub', href: github, icon: 'github', tone: 'accent' },
    { label: 'LinkedIn', href: linkedin, icon: 'linkedin', tone: 'accent' },
    { label: email, href: `mailto:${email}`, icon: 'mail', tone: 'neutral' },
    {
      label: phone,
      href: `tel:${phone.replace(/\s/g, '')}`,
      icon: 'phone',
      tone: 'neutral',
    },
  ],
  // Normalise the heterogeneous skills map into ordered {label, items} groups.
  skillGroups: ({ skills }) =>
    Object.entries(skills).map(([label, items]) => ({
      label,
      items: [...items],
    })),
};

// ---------------------------------------------------------------------------
// experience.json  →  experience[] view-model
// ---------------------------------------------------------------------------
export const experienceSchema = {
  id: 'id',
  role: 'position',
  company: 'company',
  location: 'location',
  // dayjs turns "2022-03" into a pretty range like "Mar 2022 — Present".
  period: ({ startDate, endDate, current }) => formatRange(startDate, endDate, current),
  years: ({ startDate, endDate, current }) => {
    const finish = current ? dayjs() : dayjs(endDate);
    const y = finish.diff(dayjs(startDate), 'year');
    return `${y >= 1 ? y : '< 1'} yr`;
  },
  description: 'description',
  tags: 'technologies',
};

// ---------------------------------------------------------------------------
// work.json  →  work[] view-model  (case studies)
// ---------------------------------------------------------------------------
export const workSchema = {
  id: 'id',
  title: 'title',
  role: 'role',
  excerpt: 'excerpt',
  body: 'body',
  thumbnail: 'thumbnail',
  gallery: 'gallery',
  technologies: 'technologies',
  links: asLinkList,
};

// ---------------------------------------------------------------------------
// projects.json  →  projects[] view-model
// ---------------------------------------------------------------------------
export const projectsSchema = {
  id: 'id',
  title: 'title',
  description: 'description',
  thumbnail: 'thumbnail',
  screenshots: 'screenshots',
  technologies: 'technologies',
  tags: 'tags',
  links: asLinkList,
};

// ---------------------------------------------------------------------------
// academic.json  →  academic[] view-model
// Handles two raw shapes (degrees & certifications) and unifies them.
// ---------------------------------------------------------------------------
const isCert = (s) => (s && s.type) === 'certification';

export const academicSchema = {
  id: 'id',
  type: ({ type }) => (type || 'degree'),
  // degree  → degree title ; certification → cert title
  title: ({ type, title, degree }) => (isCert({ type }) ? title : degree),
  // degree  → institution  ; certification → issuer
  institution: ({ type, institution, issuer }) =>
    isCert({ type }) ? issuer : institution,
  location: 'location',
  period: ({ type, startDate, endDate, date }) =>
    isCert({ type })
      ? dayjs(date).format('MMM YYYY')
      : formatRange(startDate, endDate, !endDate),
  subtitle: ({ type, grade, credentialId }) =>
    isCert({ type }) ? `Credential • ${credentialId}` : grade,
  body: ({ type, description, body }) => (isCert({ type }) ? body : description),
  url: 'url',
};

// ---------------------------------------------------------------------------
// awards.json  →  awards[] view-model
// ---------------------------------------------------------------------------
export const awardsSchema = {
  title: 'title',
  issuer: 'issuer',
  date: 'date',
  description: 'description',
  url: 'url'
};

// ---------------------------------------------------------------------------
// Public transform entry-point: apply a schema to a source.
// (Thin wrapper so the renderer imports one function.)
// ---------------------------------------------------------------------------
export function transform(schema, source) {
  return morphism(schema, source);
}
